// Vue GIT (LOT D-panel) partagée du plugin cli.
// Miroirs TS des records C# (GitTools.cs, camelCase wire).
import { Show, For, createSignal, createMemo, createEffect, type Component } from "solid-js";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { Collapsible } from "@/shared/collapsible";
import { fmtDateTime } from "./format";
import type { GitBranchesResult, GitDiffResult, GitLogEntry, GitOpResult, GitStatusResult } from "./types";

export interface GitViewProps {
  client: ReturnType<typeof useToolClient>;
  /** cwd courant (= workspace sélectionné). */
  currentCwd: () => string;
  /** Permet d'éditer manuellement le cwd depuis la vue Git. */
  onSelectCwd: (cwd: string) => void;
}

// Visionneuse de diff colorisée (+/-) en mono.
export const DiffViewer: Component<{ raw: string }> = (props) => {
  const lines = createMemo(() => props.raw.split("\n"));
  function lineClass(l: string): string {
    if (l.startsWith("+++") || l.startsWith("---")) return "cli-diff-line cli-diff-file";
    if (l.startsWith("@@")) return "cli-diff-line cli-diff-hunk";
    if (l.startsWith("diff ") || l.startsWith("index ")) return "cli-diff-line cli-diff-meta";
    if (l.startsWith("+")) return "cli-diff-line cli-diff-add";
    if (l.startsWith("-")) return "cli-diff-line cli-diff-del";
    return "cli-diff-line";
  }
  return (
    <pre class="cli-diff">
      <For each={lines()}>{(l) => <div class={lineClass(l)}>{l || " "}</div>}</For>
    </pre>
  );
};

export const GitView: Component<GitViewProps> = (props) => {
  const confirm = useConfirm();
  const [status, setStatus] = createSignal<GitStatusResult | null>(null);
  const [log, setLog] = createSignal<GitLogEntry[]>([]);
  const [branches, setBranches] = createSignal<GitBranchesResult | null>(null);
  const [diff, setDiff] = createSignal<{ path: string; raw: string; staged: boolean } | null>(null);
  const [loading, setLoading] = createSignal(false);
  const [busy, setBusy] = createSignal(""); // op d'écriture en cours
  const [err, setErr] = createSignal("");
  const [commitMsg, setCommitMsg] = createSignal("");
  const [cwdDraft, setCwdDraft] = createSignal("");

  const cwd = createMemo(() => props.currentCwd().trim());

  function isoNow() {
    return new Date().toISOString();
  }

  // Recharge status + branches (+ log léger) pour le cwd courant.
  async function refreshAll() {
    const dir = cwd();
    if (!dir) {
      setErr("Sélectionnez un workspace (sous-onglet Workspaces) ou saisissez un cwd.");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      const [st, br, lg] = await Promise.all([
        props.client.call("git_status", { cwd: dir }) as Promise<GitStatusResult>,
        props.client.call("git_branch_list", { cwd: dir }) as Promise<GitBranchesResult>,
        props.client.call("git_log", { cwd: dir, limit: 50, graph: true }) as Promise<{
          ok: boolean;
          entries?: GitLogEntry[];
          error?: string;
        }>,
      ]);
      setStatus(st);
      setBranches(br);
      setLog(lg.entries || []);
      if (st && st.ok === false && st.error) setErr(st.error);
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  // Recharge à chaque changement de cwd (workspace sélectionné ailleurs).
  createEffect(() => {
    const dir = cwd();
    if (dir) void refreshAll();
    else {
      setStatus(null);
      setLog([]);
      setBranches(null);
      setDiff(null);
    }
  });

  async function viewDiff(path: string, staged: boolean) {
    const dir = cwd();
    if (!dir) return;
    setErr("");
    try {
      const r = (await props.client.call("git_diff", { cwd: dir, path, staged })) as GitDiffResult;
      setDiff({ path, raw: r.raw || "(diff vide)", staged });
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }

  // git_add (stage) — DESTRUCTIF (modifie l'index).
  async function stage(path: string) {
    await writeOp("git_add", { cwd: cwd(), paths: [path] }, "stage", `Stager ${path} ?`);
  }
  // unstage = git_reset HEAD ; non listé dans les contrats → on passe par git_add avec
  // l'option d'unstage non disponible. À la place : reset via git_add inverse non supporté.
  // Le contrat n'expose pas d'outil unstage dédié → on utilise git_add { paths, unstage:true }
  // (drapeau optionnel côté handler) ; fallback documenté si non géré.
  async function unstage(path: string) {
    await writeOp("git_add", { cwd: cwd(), paths: [path], unstage: true }, "unstage", `Retirer ${path} de l'index ?`);
  }

  // Helper générique pour une opération git en écriture (DESTRUCTIVE → userConfirmedAt).
  async function writeOp(
    tool: string,
    params: Record<string, unknown>,
    tag: string,
    confirmBody: string,
    level: "warn" | "danger" = "warn",
  ) {
    if (!cwd()) return;
    const ok = await confirm.confirm({
      title: `Git — ${tool}`,
      body: confirmBody,
      level,
      confirmLabel: "Exécuter",
    });
    if (!ok) return;
    setBusy(tag);
    setErr("");
    try {
      const r = (await props.client.call(tool, params, { userConfirmedAt: isoNow() })) as GitOpResult;
      if (r && r.ok === false && r.error) setErr(r.error);
      await refreshAll();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy("");
    }
  }

  async function commit() {
    const msg = commitMsg().trim();
    if (!msg) {
      setErr("Message de commit vide.");
      return;
    }
    await writeOp("git_commit", { cwd: cwd(), message: msg }, "commit", `Committer avec le message : « ${msg} » ?`);
    setCommitMsg("");
  }

  async function checkout(name: string) {
    await writeOp(
      "git_branch_checkout",
      { cwd: cwd(), name },
      "checkout",
      `Basculer sur la branche « ${name} » ? Les changements non commités peuvent être perdus.`,
      "danger",
    );
  }

  return (
    <div class="cli-git">
      {/* Sélecteur cwd (= workspace courant) */}
      <div class="cli-toolbar cli-git-cwd-row">
        <input
          class="panel-filter-input cli-git-cwd-input"
          value={cwdDraft() || cwd()}
          placeholder="Répertoire git (cwd) — sélectionnez un workspace ou saisissez…"
          autocomplete="off"
          spellcheck={false}
          onInput={(e) => setCwdDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && cwdDraft().trim()) props.onSelectCwd(cwdDraft().trim());
          }}
        />
        <button
          class="btn btn--sm"
          onClick={() => cwdDraft().trim() && props.onSelectCwd(cwdDraft().trim())}
          disabled={!cwdDraft().trim()}
        >
          Définir cwd
        </button>
        <button class="btn btn--sm btn--ghost" onClick={() => void refreshAll()} disabled={loading() || !cwd()}>
          {loading() ? "Chargement…" : "Actualiser"}
        </button>
      </div>

      <Show when={err()}>
        <div class="panel-error">{err()}</div>
      </Show>

      <Show
        when={cwd()}
        fallback={
          <div class="panel-empty">
            Sélectionnez un workspace dans le sous-onglet « Workspaces » ou saisissez un cwd.
          </div>
        }
      >
        {/* Branche + ahead/behind + actions globales */}
        <div class="cli-git-head">
          <span class="cli-git-branch">⎇ {status()?.branch || "(detached)"}</span>
          <Show when={status() && (status()!.ahead > 0 || status()!.behind > 0)}>
            <span class="cli-git-track">
              {status()!.ahead > 0 ? `↑${status()!.ahead}` : ""} {status()!.behind > 0 ? `↓${status()!.behind}` : ""}
            </span>
          </Show>
          <Show when={status()?.upstream}>
            <span class="cli-git-upstream">→ {status()!.upstream}</span>
          </Show>
          <span class="cli-git-actions">
            <button
              class="btn btn--xs btn--warn"
              disabled={busy() === "pull"}
              onClick={() => void writeOp("git_pull", { cwd: cwd() }, "pull", "Exécuter git pull ?")}
            >
              {busy() === "pull" ? "Pull…" : "Pull"}
            </button>
            <button
              class="btn btn--xs btn--warn"
              disabled={busy() === "push"}
              onClick={() => void writeOp("git_push", { cwd: cwd() }, "push", "Exécuter git push ?")}
            >
              {busy() === "push" ? "Push…" : "Push"}
            </button>
            <button
              class="btn btn--xs btn--warn"
              disabled={busy() === "stash"}
              onClick={() =>
                void writeOp("git_stash", { cwd: cwd() }, "stash", "Remiser (git stash) les changements ?")
              }
            >
              {busy() === "stash" ? "Stash…" : "Stash"}
            </button>
          </span>
        </div>

        {/* 3 listes : staged / unstaged / untracked */}
        <div class="cli-git-lists">
          <div class="cli-git-col">
            <div class="cli-git-col-title">Staged ({status()?.staged.length || 0})</div>
            <Show when={(status()?.staged.length || 0) > 0} fallback={<div class="cli-git-empty">—</div>}>
              <For each={status()!.staged}>
                {(f) => (
                  <div class="cli-git-file">
                    <span class="cli-git-xy" data-xy={f.xy}>
                      {f.xy}
                    </span>
                    <span class="cli-git-path" title={f.path} onClick={() => void viewDiff(f.path, true)}>
                      {f.path}
                    </span>
                    <button
                      class="btn btn--xs btn--ghost"
                      onClick={() => void unstage(f.path)}
                      disabled={busy() === "unstage"}
                    >
                      Unstage
                    </button>
                  </div>
                )}
              </For>
            </Show>
          </div>

          <div class="cli-git-col">
            <div class="cli-git-col-title">Unstaged ({status()?.unstaged.length || 0})</div>
            <Show when={(status()?.unstaged.length || 0) > 0} fallback={<div class="cli-git-empty">—</div>}>
              <For each={status()!.unstaged}>
                {(f) => (
                  <div class="cli-git-file">
                    <span class="cli-git-xy" data-xy={f.xy}>
                      {f.xy}
                    </span>
                    <span class="cli-git-path" title={f.path} onClick={() => void viewDiff(f.path, false)}>
                      {f.path}
                    </span>
                    <button class="btn btn--xs" onClick={() => void stage(f.path)} disabled={busy() === "stage"}>
                      Stage
                    </button>
                  </div>
                )}
              </For>
            </Show>
          </div>

          <div class="cli-git-col">
            <div class="cli-git-col-title">Untracked ({status()?.untracked.length || 0})</div>
            <Show when={(status()?.untracked.length || 0) > 0} fallback={<div class="cli-git-empty">—</div>}>
              <For each={status()!.untracked}>
                {(p) => (
                  <div class="cli-git-file">
                    <span class="cli-git-xy" data-xy="??">
                      ??
                    </span>
                    <span class="cli-git-path" title={p} onClick={() => void viewDiff(p, false)}>
                      {p}
                    </span>
                    <button class="btn btn--xs" onClick={() => void stage(p)} disabled={busy() === "stage"}>
                      Stage
                    </button>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </div>

        {/* Zone commit */}
        <div class="cli-git-commit-row">
          <input
            class="panel-filter-input cli-git-commit-input"
            value={commitMsg()}
            placeholder="Message de commit…"
            autocomplete="off"
            spellcheck={false}
            onInput={(e) => setCommitMsg(e.currentTarget.value)}
            onKeyDown={(e) => e.key === "Enter" && commitMsg().trim() && void commit()}
          />
          <button
            class="btn btn--sm btn--warn"
            onClick={() => void commit()}
            disabled={!commitMsg().trim() || (status()?.staged.length || 0) === 0 || busy() === "commit"}
            title={
              (status()?.staged.length || 0) === 0
                ? "Rien dans l'index — stagez d'abord des fichiers"
                : "Committer l'index"
            }
          >
            {busy() === "commit" ? "Commit…" : "Commit"}
          </button>
        </div>

        {/* Diff viewer (au clic d'un fichier) */}
        <Show when={diff()}>
          {(d) => (
            <Collapsible
              variant="default"
              defaultOpen={true}
              class="cli-git-diff-block"
              header={
                <span>
                  diff — <span class="cli-git-diff-path">{d().path}</span>
                  {d().staged ? " (staged)" : ""}
                </span>
              }
            >
              <DiffViewer raw={d().raw} />
            </Collapsible>
          )}
        </Show>

        {/* Branches (current en gras + checkout) */}
        <Collapsible
          variant="default"
          class="cli-git-branches-block"
          header={<span>⎇ Branches ({(branches()?.local.length || 0) + (branches()?.remote.length || 0)})</span>}
        >
          <Show when={branches()} fallback={<div class="cli-git-empty">—</div>}>
            <div class="cli-git-branches">
              <For each={branches()!.local}>
                {(b) => (
                  <div class="cli-git-branch-row" data-current={b.current || undefined}>
                    <span class="cli-git-branch-name">{b.name}</span>
                    <Show when={!b.current}>
                      <button
                        class="btn btn--xs btn--ghost"
                        onClick={() => void checkout(b.name)}
                        disabled={busy() === "checkout"}
                      >
                        Checkout
                      </button>
                    </Show>
                    <Show when={b.current}>
                      <span class="cli-git-branch-cur">courante</span>
                    </Show>
                  </div>
                )}
              </For>
              <For each={branches()!.remote}>
                {(b) => (
                  <div class="cli-git-branch-row cli-git-branch-remote">
                    <span class="cli-git-branch-name">{b.name}</span>
                    <button
                      class="btn btn--xs btn--ghost"
                      onClick={() => void checkout(b.name)}
                      disabled={busy() === "checkout"}
                    >
                      Checkout
                    </button>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Collapsible>

        {/* Historique (git_log graph) */}
        <Collapsible variant="default" class="cli-git-log-block" header={<span>🕑 Historique ({log().length})</span>}>
          <Show when={log().length > 0} fallback={<div class="cli-git-empty">Aucun commit.</div>}>
            <div class="cli-git-log">
              <For each={log()}>
                {(c) => (
                  <div class="cli-git-log-row" title={c.subject}>
                    <span class="cli-git-log-sha">{c.sha.slice(0, 8)}</span>
                    <span class="cli-git-log-subject">{c.subject}</span>
                    <span class="cli-git-log-author">{c.author}</span>
                    <span class="cli-git-log-date">{fmtDateTime(c.date) || c.date}</span>
                  </div>
                )}
              </For>
            </div>
          </Show>
        </Collapsible>
      </Show>
    </div>
  );
};
